import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { User } from '../../models/user';
import { Page } from '../../models/page';
import { LocationDataPoint } from '../../models/location-data-point';
import { SnapToRoadResponse } from '../../models/snapped-route';
import { environment } from '../../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class UserHttpService {
  private static readonly baseurl = environment.baseurl;

  constructor(private readonly httpClient: HttpClient) {}

  getAllUsers() {
    return this.httpClient.get<User[]>(`${UserHttpService.baseurl}/api/users`);
  }

  registerUser(user: User) {
    return this.httpClient.post<User>(
      `${UserHttpService.baseurl}/api/auth/signup/${user.role.toLowerCase()}`,
      user,
    );
  }

  deleteUser(id: string) {
    return this.httpClient.delete(`${UserHttpService.baseurl}/api/users/${id}`);
  }

  updateUser(user: UpdateUserDto) {
    return this.httpClient.put<User>(`${UserHttpService.baseurl}/api/users`, user);
  }

  findUserById(id: string) {
    return this.httpClient.get<User>(`${UserHttpService.baseurl}/api/users/${id}`);
  }

  /**
   * Substring match on a rep's name or email, whichever the term looks like. The endpoint
   * checks `email` first and ignores `name` when both are given, so exactly one is sent.
   */
  searchReps(term: string) {
    const params = new HttpParams().set(term.includes('@') ? 'email' : 'name', term);
    return this.httpClient.get<User[]>(`${UserHttpService.baseurl}/api/users/search/reps`, {
      params,
    });
  }

  getUserLocationHistory(params: {
    userId: string;
    start: Date;
    end: Date;
    page: number;
    size: number;
  }) {
    return this.httpClient.get<Page<LocationDataPoint>>(
      `${UserHttpService.baseurl}/api/location-tracker`,
      {
        params: {
          ...params,
          start: params.start.toISOString().slice(0, -1),
          end: params.end.toISOString().slice(0, -1),
        },
      },
    );
  }

  snapToRoad(points: LocationDataPoint[]) {
    return this.httpClient.post<SnapToRoadResponse>(
      `${UserHttpService.baseurl}/api/location-tracker/snap-to-road`,
      { points },
    );
  }
}

export type UpdateUserDto = Omit<User, 'role'>;
